import { useState, useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import {
  useGetInitialViewDetailedQuery,
  useLazyExpandNodeDetailedQuery,
  DetailedGraphNode,
  DetailedGraphEdge,
} from '../redux/api/entityApi';
import { setLoadingEntities, setLoadingConnections } from '../redux/slices/siteSlice';

// Entity format compatible with NetworkViewerTab's processing
export interface MappedEntity {
  entity_id: string;
  entity_name: string;
  site_id: string;
  pvcollada_component_type: string;
  entity_metadata: Record<string, unknown> | null;
  aliases: string[];
  configuration: Record<string, unknown> | null;
  pref: Record<string, unknown> | null;
  component_order: number | null;
  // Lazy loading metadata
  api_child_count?: number;
  api_tier?: number;
}

// Connection format compatible with NetworkViewerTab's processing
export interface MappedConnection {
  id: string;
  site_id: string;
  source_entity_id: string;
  target_entity_id: string;
  connection_type: string;
  properties: Record<string, unknown> | null;
  configuration: Record<string, unknown> | null;
}

/**
 * Hook for lazy-loading detailed network graph data.
 *
 * This hook provides progressive loading of network data:
 * - Initial load fetches the full hierarchy with full entity details
 * - Returns data in format compatible with NetworkViewerTab's processing
 * - Supports on-demand expansion for very large sites
 *
 * Performance benefits over full entity/connection loading:
 * - Single initial request instead of 50+ paginated requests
 * - Progressive loading reduces memory footprint
 * - On-demand expansion for large hierarchies
 */
export const useLazyDetailedGraph = (
  excludeModules: boolean = true,
  includeBelowInverter: boolean = false
) => {
  const dispatch = useAppDispatch();
  const currentSite = useAppSelector((state) => state.site.currentSite);

  const portfolioId = currentSite?.portfolio_id;
  const siteId = currentSite?.site_id;

  // Track expanded nodes and accumulated data
  const [expandedNodeIds, setExpandedNodeIds] = useState<Set<string>>(new Set());
  const [additionalNodes, setAdditionalNodes] = useState<DetailedGraphNode[]>([]);
  const [additionalEdges, setAdditionalEdges] = useState<DetailedGraphEdge[]>([]);

  // Last expansion result for incremental graph updates (bypasses full processData)
  const [lastExpansionResult, setLastExpansionResult] = useState<{
    parentId: string;
    children: DetailedGraphNode[];
    edges: DetailedGraphEdge[];
  } | null>(null);

  // Initial view query - by default only loads up to inverter level
  const {
    data: initialData,
    isFetching: isLoadingInitial,
    error: initialError,
  } = useGetInitialViewDetailedQuery(
    {
      portfolio_id: portfolioId!,
      site_id: siteId!,
      exclude_modules: excludeModules,
      include_below_inverter: includeBelowInverter,
    },
    { skip: !portfolioId || !siteId, refetchOnMountOrArgChange: true }
  );

  // Lazy trigger for expanding nodes
  const [triggerExpand, { isFetching: isExpanding }] = useLazyExpandNodeDetailedQuery();

  // Reset state when site changes
  useEffect(() => {
    setExpandedNodeIds(new Set());
    setAdditionalNodes([]);
    setAdditionalEdges([]);
    setLastExpansionResult(null);
  }, [portfolioId, siteId]);

  // NOTE: expandedNodeIds only tracks nodes expanded via the expand-detailed API,
  // NOT nodes from the initial response. This ensures clicking a node always
  // triggers the API on first click to fetch its full children.

  // Combine initial data with additional expanded data
  const allNodes = useMemo(() => {
    if (isLoadingInitial) return [];
    const initial = initialData?.nodes || [];
    // Merge and deduplicate by primary_id
    const nodeMap = new Map<string, DetailedGraphNode>();
    initial.forEach(n => nodeMap.set(n.primary_id, n));
    additionalNodes.forEach(n => nodeMap.set(n.primary_id, n));
    return Array.from(nodeMap.values());
  }, [initialData?.nodes, additionalNodes, isLoadingInitial]);

  const allEdges = useMemo(() => {
    if (isLoadingInitial) return [];
    const initial = initialData?.edges || [];
    // Merge and deduplicate by id
    const edgeMap = new Map<string, DetailedGraphEdge>();
    initial.forEach(e => edgeMap.set(e.id, e));
    additionalEdges.forEach(e => edgeMap.set(e.id, e));
    return Array.from(edgeMap.values());
  }, [initialData?.edges, additionalEdges, isLoadingInitial]);

  // Track which node is currently being expanded (for loading indicator)
  const [expandingNodeId, setExpandingNodeId] = useState<string | null>(null);

  // Expand a node to load its children
  const expandNode = useCallback(async (nodeId: string) => {
    if (!portfolioId || !siteId) {
      return;
    }
    if (expandedNodeIds.has(nodeId)) {
      return; // Already expanded
    }

    setExpandingNodeId(nodeId);
    try {
      const result = await triggerExpand({
        portfolio_id: portfolioId,
        site_id: siteId,
        node_id: nodeId,
        depth: 3,
        exclude_modules: excludeModules,
      }).unwrap();

      if (result) {
        setAdditionalNodes(prev => [...prev, ...result.children]);
        setAdditionalEdges(prev => [...prev, ...result.edges]);
        setExpandedNodeIds(prev => new Set([...prev, nodeId]));
        // Expose expansion result for incremental graph update
        setLastExpansionResult({
          parentId: nodeId,
          children: result.children,
          edges: result.edges,
        });
      }
    } catch (error) {
      console.error(`Failed to expand node ${nodeId}:`, error);
    } finally {
      setExpandingNodeId(null);
    }
  }, [portfolioId, siteId, expandedNodeIds, triggerExpand]);

  // Collapse a node (remove its children from view)
  const collapseNode = useCallback((nodeId: string) => {
    if (!expandedNodeIds.has(nodeId)) return;

    // Find all descendant nodes to remove
    const nodesToRemove = new Set<string>();
    const edgesToRemove = new Set<string>();

    // BFS to find all descendants
    const queue = [nodeId];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      // Find children of current node
      allEdges.forEach(edge => {
        if (edge.target_id === currentId) {
          // edge.source_id is a child of currentId
          nodesToRemove.add(edge.source_id);
          edgesToRemove.add(edge.id);
          queue.push(edge.source_id);
        }
      });
    }

    // Remove collapsed nodes/edges from additional data
    setAdditionalNodes(prev => prev.filter(n => !nodesToRemove.has(n.primary_id)));
    setAdditionalEdges(prev => prev.filter(e => !edgesToRemove.has(e.id)));
    setExpandedNodeIds(prev => {
      const next = new Set(prev);
      next.delete(nodeId);
      // Also remove any expanded descendants
      nodesToRemove.forEach(id => next.delete(id));
      return next;
    });
  }, [expandedNodeIds, allEdges]);

  // Clear the last expansion result (called after incremental update is applied)
  const clearLastExpansionResult = useCallback(() => {
    setLastExpansionResult(null);
  }, []);

  // Combined loading state
  const isLoading = isLoadingInitial;

  // Sync loading state with Redux
  useEffect(() => {
    dispatch(setLoadingEntities(isLoading));
    dispatch(setLoadingConnections(isLoading));
  }, [isLoading, dispatch]);

  // Map DetailedGraphNode to entity format expected by NetworkViewerTab
  const entities = useMemo((): MappedEntity[] => {
    if (isLoading) return [];
    return allNodes.map((node: DetailedGraphNode) => ({
      entity_id: node.primary_id, // Use primary_id as the stable ID
      entity_name: node.primary_id,
      site_id: node.site_id,
      pvcollada_component_type: node.component_type || 'unknown',
      entity_metadata: node.properties,
      aliases: node.aliases || [],
      configuration: node.configuration,
      pref: node.pref,
      component_order: node.component_order,
      // Lazy loading metadata
      api_child_count: node.child_count,
      api_tier: node.tier,
    }));
  }, [allNodes, isLoading]);

  // Map DetailedGraphEdge to connection format expected by NetworkViewerTab
  const connections = useMemo((): MappedConnection[] => {
    if (isLoading) return [];
    return allEdges.map((edge: DetailedGraphEdge) => ({
      id: edge.id,
      site_id: siteId || '',
      source_entity_id: edge.source_id, // primary_id
      target_entity_id: edge.target_id, // primary_id
      connection_type: edge.relation || 'connects',
      properties: edge.signal ? { ...edge.properties, signal: edge.signal } : edge.properties,
      configuration: edge.configuration,
    }));
  }, [allEdges, siteId, isLoading]);

  return {
    // Data compatible with NetworkViewerTab's existing processing
    entities,
    connections,
    isLoadingEntities: isLoading,
    isLoadingConnections: isLoading,

    // Raw API data for components that need it
    rawNodes: allNodes,
    rawEdges: allEdges,

    // Metadata
    totalNodeCount: initialData?.total_node_count || 0,
    rootIds: initialData?.root_ids || [],

    // Lazy loading controls
    isExpanding,
    expandingNodeId,
    error: initialError,
    expandNode,
    collapseNode,
    expandedNodeIds,

    // Incremental expansion data
    lastExpansionResult,
    clearLastExpansionResult,
  };
};
